import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Card from 'reactstrap/lib/Card';

import {
  IncentivesCreativeConfigDefaultProps,
  IncentivesCreativeConfigPropTypes,
} from 'client/data/models/native-ads-creative-config';
import {
  CommonAdDefaultProps,
  CommonAdPropTypes,
  SiteServedAdDefaultProps,
  SiteServedAdPropTypes,
} from 'site-modules/shared/components/ad-unit/ad-unit-prop-types';
import { INCENTIVES_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { DfpTracking } from 'client/site-modules/shared/components/native-ad/utils/dfp-tracking';
import { addZipCode, definePlatform } from 'site-modules/shared/components/native-ad/utils/utils';
import {
  getDisplayHeadline,
  getTrackingValue,
} from 'site-modules/shared/components/native-ad/utils/offer-incentives-ad-utils';
import { siteServedAdTrackingWrapper } from 'site-modules/shared/components/native-ad/site-served-ad-tracking-wrapper/site-served-ad-tracking-wrapper';

import { CtaUI } from 'site-modules/shared/components/native-ad/multi-cta-ad/components/cta';

const DEFAULT_HEADLINE = 'See Offers %%PATTERN:zip%%';

export function IncentivesSiteServedAdCtaOnlyUI({
  adRef,
  adClickTrackingListener,
  adLoadTrackingListener,
  adTrackers,
  creativeConfig = IncentivesCreativeConfigDefaultProps,
  customTargeting = CommonAdDefaultProps.customTargeting,
  mobile = SiteServedAdDefaultProps.mobile,
  position = SiteServedAdDefaultProps.position,
  ads11455Ads11617Recipe = null,
  onAdLoadedCallback,
}) {
  const {
    siteServedAdsUpdater,
    zipCode,
    ipZipCode,
    offersNumber,
    endDate,
    location,
    creativeConfigData: { headline, headline2, cta, lineItemId, siteServedCreativeId, target },
    traffickingData: { clickTracker },
  } = creativeConfig;

  const loadTrackingListener = useCallback(
    event => {
      const trackingValue = getTrackingValue(headline, offersNumber, zipCode, ipZipCode, DEFAULT_HEADLINE, 15, 15);
      adLoadTrackingListener(event, trackingValue);
    },
    [adLoadTrackingListener, headline, ipZipCode, offersNumber, zipCode]
  );
  const isAds11455Ads11617Enabled = ['chal1', 'chal2'].includes(ads11455Ads11617Recipe) && !!offersNumber && !!endDate;
  const displayHeadline = getDisplayHeadline(
    headline,
    offersNumber,
    zipCode,
    ipZipCode,
    DEFAULT_HEADLINE,
    15,
    15,
    location
  );

  useEffect(() => {
    const headlineToUse =
      isAds11455Ads11617Enabled && ads11455Ads11617Recipe === 'chal2'
        ? `${offersNumber} Offers expiring <span class="text-red-40 fw-medium">${endDate}</span>`
        : displayHeadline;
    onAdLoadedCallback({ headline: headlineToUse, headline2 });
  }, [
    displayHeadline,
    ads11455Ads11617Recipe,
    offersNumber,
    endDate,
    isAds11455Ads11617Enabled,
    onAdLoadedCallback,
    headline2,
  ]);

  if (!cta) {
    return null;
  }

  const ctaURL = addZipCode(clickTracker[definePlatform(mobile)], zipCode);
  const ctaTarget = target || '_blank';
  const nativeStyle = INCENTIVES_AD.NATIVE_STYLE;

  return (
    <Card
      className="incentives-site-served pos-r"
      data-tracking-parent={nativeStyle}
      data-viewability={`${nativeStyle}-${position}`}
      innerRef={adRef}
      style={{ border: 'none' }}
    >
      <DfpTracking
        lineItemId={lineItemId}
        siteServedCreativeId={siteServedCreativeId}
        mobile={mobile}
        nativeStyle={nativeStyle}
        position={position}
        customTargeting={customTargeting}
        slotRenderEndListener={loadTrackingListener}
        siteServedAdsUpdater={siteServedAdsUpdater}
      />
      {adTrackers}
      <CtaUI
        ctaURL={ctaURL}
        ctaTarget={ctaTarget}
        adClickTrackingListener={adClickTrackingListener}
        cta={'View Offers'}
      />
    </Card>
  );
}

IncentivesSiteServedAdCtaOnlyUI.propTypes = {
  ...SiteServedAdPropTypes,
  customTargeting: CommonAdPropTypes.customTargeting,
  creativeConfig: IncentivesCreativeConfigPropTypes,
  cardWrapper: PropTypes.string,
  onAdLoadedCallback: PropTypes.func.isRequired,
  ads11455Ads11617Recipe: PropTypes.string,
};

export const IncentivesSiteServedAdCtaOnly = siteServedAdTrackingWrapper(IncentivesSiteServedAdCtaOnlyUI);
